import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheck,
  faBolt,
  faClock,
  faMapMarkerAlt,
  faTruck,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { CircularProgress } from "@mui/material";
import Card from "../ui/Card";
import ActionButton from "../ui/ActionButton";
import QuickAddGrid from "./QuickAddGrid";
import { useProfileData } from "../../hooks/useProfileData";
import { useMaterialTypes } from "../../hooks/useMaterialTypes";
import "../../style/DetailedViewPanel.css";
import "../../style/QuickAddPanel.css";

const DAYS_CONFIG = [
  { key: "monday", label: "M" },
  { key: "tuesday", label: "T" },
  { key: "wednesday", label: "W" },
  { key: "thursday", label: "Th" },
  { key: "friday", label: "F" },
  { key: "saturday", label: "S" },
  { key: "sunday", label: "Su" },
];

const DEFAULT_BATCH = {
  pickup_time_start: "09:00",
  pickup_time_end: "17:00",
  available_days: {
    monday: true, tuesday: true, wednesday: true,
    thursday: true, friday: true, saturday: false, sunday: false,
  },
  delivery_method: "pickup",
  transport_mode: "",
  delivery_instructions: "",
  use_default_address: true,
  address: "",
  city: "",
  share_contact: true,
  contact_email: "",
  contact_phone: "",
};

const QuickAddPanel = ({ darkMode, onClose, onSubmit, isSubmitting, initialRows, initialBatch }) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const [rows, setRows] = useState(() =>
    initialRows && initialRows.length > 0 ? initialRows : [emptyRow()]
  );
  const [batch, setBatch] = useState(() =>
    initialBatch ? { ...DEFAULT_BATCH, ...initialBatch } : DEFAULT_BATCH
  );
  const [errors, setErrors] = useState({ rows: {}, batch: {} });

  const { profile, loading: profileLoading } = useProfileData();
  const { materialTypes, loading: materialTypesLoading } = useMaterialTypes();

  useEffect(() => {
    if (profile && !profileLoading) {
      setBatch((prev) => ({
        ...prev,
        // only fill from profile if not already set by initialBatch
        address: prev.address || profile.address || "",
        city: prev.city || profile.city || "",
        contact_email: prev.contact_email || profile.email || "",
        contact_phone: prev.contact_phone || profile.phone_number || "",
      }));
    }
  }, [profile, profileLoading]);

  function emptyRow() {
    return { id: crypto.randomUUID(), material: "", item_name: "", quantity: "", cost: "", latest_pickup: "" };
  }

  const handleBatchChange = (field, value) => {
    setBatch((prev) => ({ ...prev, [field]: value }));
    if (errors.batch[field]) {
      setErrors((prev) => ({ ...prev, batch: { ...prev.batch, [field]: "" } }));
    }
  };

  const handleDayToggle = (day) => {
    setBatch((prev) => ({
      ...prev,
      available_days: { ...prev.available_days, [day]: !prev.available_days[day] },
    }));
    if (errors.batch.available_days) {
      setErrors((prev) => ({ ...prev, batch: { ...prev.batch, available_days: "" } }));
    }
  };

  const isRowEmpty = (row) =>
    !row.material && !row.item_name && !row.quantity && !row.cost && !row.latest_pickup;

  const validate = () => {
    const rowErrors = {};
    const batchErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // skip completely empty rows, validate partially filled ones
    let filledCount = 0;
    rows.forEach((row, i) => {
      if (isRowEmpty(row)) return;
      filledCount++;
      const re = {};
      if (!row.material) re.material = t("Required");
      if (!row.quantity) re.quantity = t("Required");
      else if (parseFloat(row.quantity) <= 0) re.quantity = t("Must be > 0");
      if (row.cost === "" || row.cost === null) re.cost = t("Required");
      else if (parseFloat(row.cost) < 0) re.cost = t("Must be ≥ 0");
      if (!row.latest_pickup) re.latest_pickup = t("Required");
      else {
        const d = new Date(row.latest_pickup);
        if (d < today) re.latest_pickup = t("Must be future");
      }
      if (Object.keys(re).length) rowErrors[i] = re;
    });

    if (filledCount === 0) {
      rowErrors._empty = t("Add at least one listing");
    }

    // batch validation
    const hasDay = Object.values(batch.available_days).some(Boolean);
    if (!hasDay) batchErrors.available_days = t("Select at least one day");

    const startMin = timeToMinutes(batch.pickup_time_start);
    const endMin = timeToMinutes(batch.pickup_time_end);
    if (startMin >= endMin) {
      batchErrors.pickup_time_start = t("Start must be before end");
    } else if (endMin - startMin < 60) {
      batchErrors.pickup_time_start = t("Minimum 1 hour window");
    }

    if (!batch.use_default_address) {
      if (!batch.address?.trim()) batchErrors.address = t("Required");
      if (!batch.city?.trim()) batchErrors.city = t("Required");
    }

    if (!batch.contact_email) {
      batchErrors.contact_email = t("Email is required");
    } else if (!/\S+@\S+\.\S+/.test(batch.contact_email)) {
      batchErrors.contact_email = t("Invalid email");
    }

    if (!batch.contact_phone?.trim()) {
      batchErrors.contact_phone = t("Phone is required");
    }

    if (batch.delivery_method === "delivery" && !batch.transport_mode?.trim()) {
      batchErrors.transport_mode = t("Required for delivery");
    }

    setErrors({ rows: rowErrors, batch: batchErrors });
    return Object.keys(rowErrors).length === 0 && Object.keys(batchErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const listings = rows
      .filter((row) => !isRowEmpty(row))
      .map((row) => ({
      material: row.material,
      item_name: row.item_name.trim() || null,
      quantity: parseFloat(row.quantity),
      cost: parseFloat(row.cost),
      latest_pickup: row.latest_pickup,
      ...(row.duplicated_from && { duplicated_from: row.duplicated_from }),
    }));

    const batchSettings = {
      available_days: batch.available_days,
      pickup_time_start: batch.pickup_time_start,
      pickup_time_end: batch.pickup_time_end,
      delivery_method: batch.delivery_method,
      transport_mode: batch.delivery_method === "delivery" ? batch.transport_mode.trim() : null,
      delivery_instructions: batch.delivery_instructions.trim(),
      share_contact: batch.share_contact,
      use_default_address: batch.use_default_address,
      address: batch.use_default_address ? null : batch.address?.trim(),
      city: batch.use_default_address ? null : batch.city?.trim(),
      // only send custom contact if different from profile
      custom_email: batch.contact_email !== profile?.email ? batch.contact_email.trim() : null,
      custom_phone: batch.contact_phone !== profile?.phone_number ? batch.contact_phone.trim() : null,
    };

    onSubmit(listings, batchSettings);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(), 300);
  };

  if (profileLoading || materialTypesLoading) {
    return (
      <Card variant="system" darkMode={darkMode} className="detailed-view-panel">
        <div className="panel-loading">
          <CircularProgress sx={{ color: "var(--role-accent)" }} />
          <span>{t("Loading...")}</span>
        </div>
      </Card>
    );
  }

  return (
    <Card
      variant="system"
      darkMode={darkMode}
      className={`detailed-view-panel quick-add-panel ${isClosing ? "closing" : ""}`}
    >
      {/* Header */}
      <div className="panel-header">
        <div className="panel-header-left">
          <h3 className="panel-title">
            <span className="panel-title-icon">
              <FontAwesomeIcon icon={faBolt} />
            </span>
            {t("Quick Add Listings")}
            <span className="panel-header-separator" />
            <span className="panel-subtitle">
              {initialRows
                ? t("Pre-filled from {{count}} selected listings — review and adjust before submitting", { count: initialRows.length })
                : t("List multiple materials in one go — share the same pickup schedule, location, and contact across all entries")}
            </span>
          </h3>
        </div>
        <button onClick={handleClose} className="close-button" aria-label="Close" disabled={isSubmitting}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Grid */}
      <div className="quick-add-grid-section">
        <QuickAddGrid
          rows={rows}
          setRows={setRows}
          materialTypes={materialTypes}
          errors={errors.rows}
          disabled={isSubmitting}
          emptyRow={emptyRow}
        />
      </div>

      {/* Batch Settings */}
      <div className="panel-content-grid quick-add-batch">
        <div className="panel-column">
          {/* Schedule */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon blue">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <h4 className="info-card-title">{t("Pickup Schedule")}</h4>
              <span className="batch-badge">{t("Shared")}</span>
            </div>
            <div className="info-card-content">
              <div className="field-row-group">
                <div className="field-row">
                  <label className="field-label">{t("From")}</label>
                  <input
                    type="time"
                    className={`field-input ${errors.batch.pickup_time_start ? "error" : ""}`}
                    value={batch.pickup_time_start}
                    onChange={(e) => handleBatchChange("pickup_time_start", e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field-row">
                  <label className="field-label">{t("To")}</label>
                  <input
                    type="time"
                    className={`field-input ${errors.batch.pickup_time_end ? "error" : ""}`}
                    value={batch.pickup_time_end}
                    onChange={(e) => handleBatchChange("pickup_time_end", e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              {errors.batch.pickup_time_start && (
                <span className="field-error">{errors.batch.pickup_time_start}</span>
              )}

              <div className="field-row">
                <label className="field-label">{t("Days")}</label>
                <div className={`days-row ${errors.batch.available_days ? "error" : ""}`}>
                  {DAYS_CONFIG.map((day) => (
                    <button
                      key={day.key}
                      type="button"
                      className={`day-chip ${batch.available_days[day.key] ? "active" : ""}`}
                      onClick={() => handleDayToggle(day.key)}
                      disabled={isSubmitting}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                {errors.batch.available_days && (
                  <span className="field-error">{errors.batch.available_days}</span>
                )}
              </div>
            </div>
          </div>

          {/* Logistics */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon green">
                <FontAwesomeIcon icon={faTruck} />
              </div>
              <h4 className="info-card-title">{t("Logistics")}</h4>
              <span className="batch-badge">{t("Shared")}</span>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label">{t("Delivery Method")}</label>
                <select
                  className="field-input"
                  value={batch.delivery_method}
                  onChange={(e) => {
                    handleBatchChange("delivery_method", e.target.value);
                    if (e.target.value !== "delivery") {
                      handleBatchChange("transport_mode", "");
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <option value="pickup">{t("Pickup")}</option>
                  <option value="delivery">{t("Delivery")}</option>
                </select>
              </div>

              {batch.delivery_method === "delivery" && (
                <div className="field-row">
                  <label className="field-label">{t("Mode of Transportation")}</label>
                  <input
                    type="text"
                    className={`field-input ${errors.batch.transport_mode ? "error" : ""}`}
                    value={batch.transport_mode}
                    onChange={(e) => handleBatchChange("transport_mode", e.target.value)}
                    placeholder={t("e.g., Car, Truck, Bike...")}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                  {errors.batch.transport_mode && (
                    <span className="field-error">{errors.batch.transport_mode}</span>
                  )}
                </div>
              )}

              <div className="field-row">
                <label className="field-label">{t("Instructions")}</label>
                <textarea
                  className="field-input field-textarea"
                  value={batch.delivery_instructions}
                  onChange={(e) => handleBatchChange("delivery_instructions", e.target.value)}
                  placeholder={
                    batch.delivery_method === "delivery"
                      ? t("Special instructions for delivery")
                      : t("Special instructions for pickup")
                  }
                  rows="3"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="panel-column">
          {/* Location */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon red">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <h4 className="info-card-title">{t("Pickup Location")}</h4>
              <span className="batch-badge">{t("Shared")}</span>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label">{t("Address")}</label>
                <input
                  type="text"
                  className={`field-input ${
                    batch.use_default_address ? "disabled" : ""
                  } ${errors.batch.address ? "error" : ""}`}
                  value={batch.address}
                  onChange={(e) => handleBatchChange("address", e.target.value)}
                  placeholder={t("Enter address")}
                  disabled={isSubmitting || batch.use_default_address}
                />
              </div>
              <div className="field-row">
                <label className="field-label">{t("City")}</label>
                <input
                  type="text"
                  className={`field-input ${
                    batch.use_default_address ? "disabled" : ""
                  } ${errors.batch.city ? "error" : ""}`}
                  value={batch.city}
                  onChange={(e) => handleBatchChange("city", e.target.value)}
                  placeholder={t("Enter city")}
                  disabled={isSubmitting || batch.use_default_address}
                />
              </div>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={batch.use_default_address}
                  onChange={(e) => handleBatchChange("use_default_address", e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="toggle-label">{t("Use default address")}</span>
              </label>
            </div>
          </div>

          {/* Contact */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon purple">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h4 className="info-card-title">{t("Contact Information")}</h4>
              <span className="batch-badge">{t("Shared")}</span>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label">{t("Email")}</label>
                <input
                  type="email"
                  className={`field-input ${
                    !batch.share_contact ? "disabled" : ""
                  } ${errors.batch.contact_email ? "error" : ""}`}
                  value={batch.contact_email}
                  onChange={(e) => handleBatchChange("contact_email", e.target.value)}
                  placeholder={t("Enter email")}
                  disabled={isSubmitting || !batch.share_contact}
                />
                {errors.batch.contact_email && (
                  <span className="field-error">{errors.batch.contact_email}</span>
                )}
              </div>
              <div className="field-row">
                <label className="field-label">{t("Phone")}</label>
                <input
                  type="tel"
                  className={`field-input ${
                    !batch.share_contact ? "disabled" : ""
                  } ${errors.batch.contact_phone ? "error" : ""}`}
                  value={batch.contact_phone}
                  onChange={(e) => handleBatchChange("contact_phone", e.target.value)}
                  placeholder={t("Enter phone")}
                  disabled={isSubmitting || !batch.share_contact}
                />
                {errors.batch.contact_phone && (
                  <span className="field-error">{errors.batch.contact_phone}</span>
                )}
              </div>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={batch.share_contact}
                  onChange={(e) => handleBatchChange("share_contact", e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="toggle-label">{t("Share contact on listings")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="panel-actions">
        {errors.rows?._empty && (
          <span className="field-error" style={{ marginRight: "auto" }}>{errors.rows._empty}</span>
        )}
        <ActionButton onClick={handleClose} variant="default" disabled={isSubmitting}>
          {t("Cancel")}
        </ActionButton>
        <ActionButton
          onClick={handleSubmit}
          variant="secondary"
          loading={isSubmitting}
          icon={faCheck}
        >
          {(() => {
            const count = rows.filter((r) => !isRowEmpty(r)).length;
            return count <= 1
              ? t("Create 1 Listing")
              : t("Create {{count}} Listings", { count });
          })()}
        </ActionButton>
      </div>
    </Card>
  );
};

function timeToMinutes(time) {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + (m || 0);
}

QuickAddPanel.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  initialRows: PropTypes.array,
  initialBatch: PropTypes.object,
};

export default QuickAddPanel;
